import {
  useState,
  useMemo,
  useRef,
  Fragment,
  useEffect,
  useCallback,
} from 'react';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { searchParser } from 'helpers';
import { useOnClickOutside } from 'hooks';
import {
  Input,
  List,
  SelectValue,
  LocationWrapper,
  DestinationWrapper,
  Destination,
  CountryName,
  Code,
  Airport,
  CityIcon,
  AirportIcon,
} from './Select.style';

const Select = ({ content, selected, setSelected, label, name }) => {
  const { t } = useTranslation();
  const [expand, setExpand] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const selectRef = useRef();
  useOnClickOutside(selectRef, () => setExpand(false));

  const defaultSearchItem = {
    city_name: t('flightstatus.search.nocity'),
    city_code: '',
  };

  useEffect(() => {
    if (!selected.name && firstLoad) {
      setSelected({
        [name]: {
          name: t('flightstatus.search.nocity'),
          codes: '',
        },
      });
      setFirstLoad(false);
    }
  }, [selected, setSelected, name, t, firstLoad]);

  const search = useMemo(() => {
    if (content && content.length > 0)
      return searchParser(content, selected?.name);
  }, [selected, content]);

  const handleSelect = (value) => {
    const { airport_code, city_name, city_code, airports, is_city } = value;
    setSelected({
      [name]: {
        name: `${city_name} ${airport_code || city_code}`?.trim(),
        codes: airport_code
          ? airport_code
          : is_city
          ? airports?.map((air) => air.code).join()
          : '',
      },
    });
    setExpand(false);
  };

  const handleChange = (e) => {
    setExpand(true);
    setSelected({
      [name]: {
        name: e.target.value?.trim(),
        codes: '',
      },
    });
  };

  const handleBlur = () => {
    if (!selected.name)
      setSelected({
        [name]: {
          name: t('flightstatus.search.nocity'),
          codes: '',
        },
      });
  };

  const handleClick = (e) => {
    if (e?.target?.select) e.target.select();
  };

  const handleUnderline = useCallback(
    (word) => {
      const name = selected?.name?.toLowerCase();
      const matchWord = word
        ?.split(' ')
        ?.find((el) => el?.toLowerCase()?.startsWith(name));
      const matchIndex = word
        ?.toLowerCase()
        ?.indexOf(matchWord?.toLowerCase(), 0);

      if (matchIndex !== -1) {
        let beginning = '',
          underlined = '',
          ending = '';
        if (matchIndex === 0) {
          underlined = word?.slice(0, name?.length);
          ending = word?.slice(name?.length);
        } else {
          beginning = word?.slice(0, matchIndex);
          underlined = word?.slice(
            beginning?.length,
            (beginning + name)?.length
          );
          ending = word?.slice((beginning + underlined)?.length);
        }

        return (
          <Fragment>
            {beginning}
            <span style={{ textDecoration: 'underline' }}>{underlined}</span>
            {ending}
          </Fragment>
        );
      } else {
        return word;
      }
    },
    [selected]
  );

  const items = useCallback(
    (el) => {
      const city = el?.is_city;

      return (
        <Fragment>
          <DestinationWrapper>
            <LocationWrapper>
              {city ? <CityIcon /> : <AirportIcon />}
              <Destination>
                {handleUnderline(
                  city
                    ? `${el?.city_name} - All airports`
                    : `${el?.city_name}${
                        el?.airport_name ? ' - ' + el.airport_name : ''
                      }`
                )}
              </Destination>
            </LocationWrapper>
            {el?.airports?.map((el) => (
              <LocationWrapper key={el?.code}>
                <AirportIcon style={{ marginLeft: '20px' }} />
                <Airport>{handleUnderline(`${el?.name} ${el?.code}`)}</Airport>
              </LocationWrapper>
            ))}
            <CountryName style={{ marginLeft: city ? '0' : '23px' }}>
              {el?.country_name}
            </CountryName>
          </DestinationWrapper>
          <Code>
            {handleUnderline(city ? el?.city_code : el?.airport_code)}
          </Code>
        </Fragment>
      );
    },
    [handleUnderline]
  );

  return (
    <Input>
      <TextField
        id={label}
        autoComplete="off"
        name={name}
        fullWidth
        value={selected.name}
        onChange={handleChange}
        variant="standard"
        label={label}
        onClick={handleClick}
        onBlur={handleBlur}
        InputProps={{
          disableUnderline: true,
          'aria-label': label,
        }}
        InputLabelProps={{
          htmlFor: label,
        }}
      />
      {expand && (
        <List ref={selectRef}>
          {search &&
            [defaultSearchItem, ...search].map((el, index) => (
              <SelectValue key={index} onClick={() => handleSelect(el)}>
                {items(el)}
              </SelectValue>
            ))}
        </List>
      )}
    </Input>
  );
};

export default Select;
